import { useRef } from "react";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import { reduce } from "et3-type-guards";
import { fontWeights, sizes } from "et3-ui-library";

import i18n from "../../constants/i18n";
import { withLiteObserverAndTheme } from "../../core/et3-enhanced-render";
import getCorePersonalStore from "../../stores/core/core-personal-store";
import getCoreUIStore from "../../stores/core/core-ui-store";
import ProfileDropdown from "../profile-dropdown/ProfileDropdown";
import SearchBar from "./SearchBar";

const UserName = withLiteObserverAndTheme((props) => {
  const { theme } = props;
  const { unitX } = theme;
  const user = getCorePersonalStore().user.get();
  const name = reduce(() => user.username, "");
  const isProfileDropdownVisible =
    getCoreUIStore().isProfileDropdownVisible.get();
  const isMobileNav = getCoreUIStore().isMobileNav.get();
  const { isSearchPopoverVisible, setIsSearchPopoverVisible } =
    getCoreUIStore();
  const searchAnchorRef = useRef<HTMLButtonElement>(null);
  const profileAnchorRef = useRef<HTMLButtonElement>(null);

  if (isMobileNav) {
    return (
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{ flexShrink: 0 }}
      >
        <IconButton
          ref={searchAnchorRef}
          size="small"
          aria-label={i18n.get("SEARCH")}
          aria-haspopup="true"
          aria-expanded={isSearchPopoverVisible.get()}
          onClick={() => setIsSearchPopoverVisible(true)}
        >
          <SearchIcon />
        </IconButton>
        <Popover
          open={isSearchPopoverVisible.get()}
          anchorEl={() => searchAnchorRef.current}
          onClose={() => setIsSearchPopoverVisible(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: { p: 1.5, width: sizes.drawerWidth } } }}
        >
          <SearchBar autoFocus />
        </Popover>
        <Tooltip title={name}>
          <IconButton
            ref={profileAnchorRef}
            size="small"
            aria-label={name}
            aria-haspopup="true"
            aria-expanded={isProfileDropdownVisible}
            onClick={() => getCoreUIStore().toggleProfileDropdown()}
          >
            <AccountCircleOutlinedIcon />
          </IconButton>
        </Tooltip>
        <ProfileDropdown anchorEl={() => profileAnchorRef.current} />
      </Stack>
    );
  }

  return (
    <Stack
      spacing={0.25}
      justifyContent="center"
      alignItems="flex-end"
      sx={{ width: 470 * unitX, minWidth: 0, flexShrink: 0 }}
    >
      <Button
        ref={profileAnchorRef}
        variant="text"
        color="inherit"
        aria-haspopup="true"
        aria-expanded={isProfileDropdownVisible}
        endIcon={
          isProfileDropdownVisible ? <ExpandLessIcon /> : <ExpandMoreIcon />
        }
        onClick={() => getCoreUIStore().toggleProfileDropdown()}
        sx={{
          textTransform: "none",
          minWidth: 0,
          maxWidth: "100%",
          py: 0,
          px: 0.5
        }}
      >
        <Typography variant="body1" fontWeight={fontWeights.medium} noWrap>
          {name}
        </Typography>
      </Button>
      <ProfileDropdown anchorEl={() => profileAnchorRef.current} />
      <SearchBar size="small" />
    </Stack>
  );
});
export default UserName;
